'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { trackROICalculatorInteraction, trackROICalculatorSubmit } from '@/lib/analytics';
import { useRouter } from 'next/navigation';

interface ROISettings {
  rewardPerReview: number;
  platformFeePct: number;
  budgetMin: number;
  budgetMax: number;
  budgetDefault: number;
  avgRating: number;
}

const DEFAULTS: ROISettings = {
  rewardPerReview: 800,
  platformFeePct: 20,
  budgetMin: 5000,
  budgetMax: 100000,
  budgetDefault: 15000,
  avgRating: 4.3,
};

interface LeadForm {
  company_name: string;
  email: string;
  phone: string;
}

const initialLead: LeadForm = { company_name: '', email: '', phone: '' };

export default function ROICalculator() {
  const [settings, setSettings] = useState<ROISettings>(DEFAULTS);
  const [budget, setBudget] = useState(DEFAULTS.budgetDefault);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [lead, setLead] = useState<LeadForm>(initialLead);
  const [submitting, setSubmitting] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('platform_settings')
      .select('key, value')
      .in('key', ['roi_reward_per_review', 'roi_platform_fee_pct', 'roi_budget_min', 'roi_budget_max', 'roi_budget_default', 'roi_avg_rating'])
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        data.forEach((r) => { map[r.key] = r.value || ''; });
        const s: ROISettings = {
          rewardPerReview: Number(map.roi_reward_per_review) || DEFAULTS.rewardPerReview,
          platformFeePct: Number(map.roi_platform_fee_pct) || DEFAULTS.platformFeePct,
          budgetMin: Number(map.roi_budget_min) || DEFAULTS.budgetMin,
          budgetMax: Number(map.roi_budget_max) || DEFAULTS.budgetMax,
          budgetDefault: Number(map.roi_budget_default) || DEFAULTS.budgetDefault,
          avgRating: Number(map.roi_avg_rating) || DEFAULTS.avgRating,
        };
        setSettings(s);
        setBudget(s.budgetDefault);
      });
  }, []);

  const reviewsEstimate = Math.floor((budget * (1 - settings.platformFeePct / 100)) / settings.rewardPerReview);
  const durationDays = Math.ceil(reviewsEstimate / 5);

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setBudget(val);
    if (!hasInteracted) {
      setHasInteracted(true);
      trackROICalculatorInteraction(val, Math.floor((val * (1 - settings.platformFeePct / 100)) / settings.rewardPerReview));
    }
  };

  const handleLeadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLead((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setLeadError(null);
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lead.email) {
      setLeadError('Please enter your email address.');
      return;
    }
    setSubmitting(true);
    setLeadError(null);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('roi_calculator_submissions').insert({
        budget,
        estimated_reviews: reviewsEstimate,
        estimated_duration_days: durationDays,
        company_name: lead.company_name || null,
        email: lead.email,
        phone: lead.phone || null,
      });
      if (dbError) {
        setLeadError('Failed to save. Please try again.');
        console.error('ROI submission error:', dbError.message);
      } else {
        trackROICalculatorSubmit(budget);
        router.push('/for-brands/submission-success?type=roi');
      }
    } catch (err: any) {
      setLeadError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="roi-calculator" className="section-py bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary bg-primary-container px-3 py-1 rounded-full mb-3">
            ROI Calculator
          </span>
          <h2 className="font-heading font-bold text-foreground" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Estimate Your Campaign Results
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">Drag the slider to see what your budget delivers</p>
        </div>

        <div className="bg-card rounded-3xl border border-border p-6 sm:p-8 space-y-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="font-heading font-semibold text-foreground text-sm">Campaign Budget</label>
              <span className="font-heading font-bold text-primary text-xl">₹{budget.toLocaleString('en-IN')}</span>
            </div>
            <input
              type="range"
              min={settings.budgetMin}
              max={settings.budgetMax}
              step={1000}
              value={budget}
              onChange={handleBudgetChange}
              className="w-full accent-primary h-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>₹{settings.budgetMin.toLocaleString('en-IN')} (Starter)</span>
              <span>₹{settings.budgetMax.toLocaleString('en-IN')} (Enterprise)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Estimated Reviews', value: `${reviewsEstimate}+`, icon: 'ChatBubbleLeftRightIcon', color: '#FF6B35', desc: 'Verified, AI-checked' },
              { label: 'Expected Avg Rating', value: `${settings.avgRating}★`, icon: 'StarIcon', color: '#FFB300', desc: 'Based on platform avg' },
              { label: 'Campaign Duration', value: `~${durationDays} days`, icon: 'CalendarDaysIcon', color: '#1A237E', desc: 'From launch to completion' },
            ].map((r) => (
              <div key={r.label} className="rounded-2xl p-5 text-center" style={{ backgroundColor: `${r.color}10` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${r.color}20` }}>
                  <Icon name={r.icon as 'ChatBubbleLeftRightIcon'} size={20} style={{ color: r.color }} />
                </div>
                <div className="font-heading font-bold text-2xl text-foreground">{r.value}</div>
                <div className="text-xs font-semibold text-foreground mt-1">{r.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{r.desc}</div>
              </div>
            ))}
          </div>

          <div className="bg-primary-container rounded-2xl p-4 flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={18} className="text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground/80">
              Estimates based on platform averages. Actual results may vary by category, product price, and reviewer availability. Platform fee of {settings.platformFeePct}% applies.
            </p>
          </div>

          {/* Lead capture */}
          {!showLeadForm && (
            <div className="text-center pt-2">
              <button
                onClick={() => setShowLeadForm(true)}
                className="cta-gradient text-white font-heading font-bold px-8 py-3 rounded-xl text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              >
                <Icon name="EnvelopeIcon" size={16} />
                Get This Estimate via Email
              </button>
              <p className="text-xs text-muted-foreground mt-2">Free, no commitment required</p>
            </div>
          )}

          {showLeadForm && (
            <form onSubmit={handleLeadSubmit} className="border border-border rounded-2xl p-5 space-y-4 bg-background">
              <h4 className="font-heading font-semibold text-foreground text-sm">Send me this estimate</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={lead.company_name}
                    onChange={handleLeadChange}
                    placeholder="Your brand"
                    className="w-full border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary bg-card transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Work Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={lead.email}
                    onChange={handleLeadChange}
                    placeholder="you@company.com"
                    required
                    className="w-full border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary bg-card transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Phone (Optional)</label>
                <input
                  type="tel"
                  name="phone"
                  value={lead.phone}
                  onChange={handleLeadChange}
                  placeholder="+91 98765 43210"
                  className="w-full border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary bg-card transition-all"
                />
              </div>
              {leadError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <Icon name="ExclamationCircleIcon" size={15} className="text-red-500 shrink-0" />
                  <span className="text-xs text-red-600">{leadError}</span>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 cta-gradient text-white font-heading font-bold py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={15} />
                      Send Estimate
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowLeadForm(false)}
                  className="px-4 py-2.5 border border-border rounded-xl text-sm text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}